// launcher-builder/launcherSlots.js — 런처 저작의 선택지 정의.
//
// ⚠️⚠️ **여기 값은 세 곳과 같아야 한다.**
//    · 런타임  LAUNCHER/src/types.ts      (TilePresentation · TileArea)
//    · 서버    SERVER/module/util/launcherConfig.js (PRESENTATIONS · TILE_AREAS)
//    · 여기
//    셋이 갈라지면 저작자가 고른 값을 서버가 버리거나(모르는 값은 기본값으로 눕는다)
//    런타임이 빈 타일을 그린다.
//
// 설계: project-le868/launcher-page-design.md §2-1 · §3-3

/**
 * 고정 셸의 그림 슬롯 (설계 §2-1). 저장 위치는 셋 다 같다(`shell.assets.{키}`) —
 * `area` 는 **어느 탭에서 저작하는지**만 가른다.
 *
 * ⚠️ **칸을 늘리지 말 것.** 자리가 스무 개면 저작자는 그림 스무 장을 구해 와야 한다.
 *    런처의 확장축은 **타일**이지 셸이 아니다 — 게임 gameSlots.js 가 적어 둔 것과 같은 생각.
 * ⚠️ 목록을 **한 벌로** 둔 이유: 서버·런타임이 보는 건 `shell.assets` 한 칸이라, 탭별로 배열을
 *    따로 적으면 키가 갈라졌을 때 아무도 못 알아챈다(저장은 되고 화면만 비는 모양이 된다).
 */
export const SHELL_SLOTS = [
	{
		key: "background",
		area: "main",
		label: "배경",
		hint: "화면을 꽉 채웁니다. 비우면 테마 바탕색만 보입니다. 다른 화면이 자기 배경을 안 정했으면 이 그림을 씁니다.",
	},
	{
		key: "mascot",
		area: "main",
		label: "마스코트",
		hint: "화면 가운데 캐릭터. 배경이 비치도록 투명 PNG 를 쓰세요. 다른 화면의 캐릭터 기본값으로도 쓰입니다.",
	},
	{
		key: "title",
		area: "main",
		label: "타이틀",
		hint: "메인 화면 맨 위 가운데 그림(행사 제목 등). 폭의 63%(390 화면에서 246)로 그려집니다. 비우면 안 나옵니다. 로그인 화면 '로고'와는 다른 칸입니다.",
	},
	{
		key: "settingsIcon",
		area: "statbar",
		label: "설정 아이콘",
		hint: "상단 오른쪽 설정 버튼의 그림. 36px 원에 꽉 채워 그리므로 테두리까지 그려진 한 장을 쓰세요. 비우면 기본 톱니 아이콘이 나옵니다.",
	},
	{
		key: "logo",
		area: "brand",
		label: "로고",
		hint: "로그인 화면의 타이틀 자리에 쓰입니다. 화면마다 다른 그림을 쓰려면 [화면] 탭에서 그 화면의 '타이틀'을 따로 지정하세요.",
	},
];

/**
 * ⭐ 메인(홈) 화면에 **보이는** 그림 — [화면] 탭에서 저작한다.
 * 메인 화면도 다른 화면들과 나란히 있어야 저작자가 프리뷰로 확인하며 만들 수 있다.
 */
export const MAIN_SLOTS = SHELL_SLOTS.filter((s) => s.area === "main");

/** 화면이 아니라 **브랜드**에 딸린 그림 — [디자인] 탭에서 테마와 함께 저작한다. */
export const BRAND_SLOTS = SHELL_SLOTS.filter((s) => s.area === "brand");

/**
 * 상단 바에 붙는 그림 — [화면] 탭의 **'상단 바' 섹션**에서 저작한다(배경·마스코트와 따로).
 * ⚠️ 보이기 스위치 바로 옆이 제자리다. 메인 화면 그림 묶음(MAIN_SLOTS)에 끼워 두면
 *    "배경·마스코트·설정아이콘" 이 한 줄로 서서 **어느 게 상단 바인지 안 보인다.**
 * ⚠️ 저장 자리는 MAIN_SLOTS 와 **같은 칸**(`shell.assets`)이다 — 서버는 슬롯키를 모르고
 *    `Object.entries(shell.assets)` 를 돌며 URL 로 풀기 때문에 **서버는 안 고쳐도 된다.**
 */
export const STATBAR_SLOTS = SHELL_SLOTS.filter((s) => s.area === "statbar");

/**
 * 타일이 화면을 여는 방법 (설계 §2-2).
 *
 * ⭐ 이 세 갈래가 "게임·챗은 입구만, 운세는 통일감"이라는 요구를 가른다 —
 *    자기 런타임이 있는 모듈은 link/embed 로 보내고, 없는 것(운세)만 native 로 품는다.
 */
export const PRESENTATIONS = [
	{
		value: "link",
		label: "주소로 이동",
		hint: "게임처럼 자기 화면이 따로 있는 모듈. 런처는 보내주는 역할만 합니다.",
	},
	{
		value: "embed",
		label: "덮어서 열기",
		hint: "채팅처럼 런처 위에 겹쳐 엽니다. 닫으면 홈으로 돌아옵니다.",
	},
	{
		value: "native",
		label: "런처가 직접 (레시피)",
		hint: "운세처럼 런처 안에서 그립니다. [레시피] 탭에서 만든 것을 고릅니다.",
	},
	{
		value: "builtin",
		label: "런처 기본 화면",
		hint: "런처에 붙박이로 들어 있는 화면입니다. 저작할 게 없습니다.",
	},
];

/**
 * ⭐ 랭킹보드가 **무엇을 줄 세우나**.
 * ⚠️ 서버 Launcher.js RANKING_SOURCES · 런타임 types.ts RankingSource 와 같아야 한다.
 * ⚠️ `needsGame` 인 소스는 **대상 게임 타일**이 반드시 따라붙는다 — 안 고르면 빈 표가 나온다.
 */
export const RANKING_SOURCES = [
	{
		value: "wallet",
		label: "재화",
		hint: "전체 기간이면 지금 보유량, 기간을 고르면 그 기간에 **번** 양으로 줄 세웁니다(쓴 건 빼지 않습니다).",
	},
	{ value: "game-best", label: "게임 최고 점수", hint: "한 판 최고 기록.", needsGame: true },
	{ value: "game-total", label: "게임 누적 점수", hint: "점수를 모두 더합니다.", needsGame: true },
	{ value: "game-plays", label: "게임 플레이 횟수", hint: "많이 한 사람 순.", needsGame: true },
];

/**
 * ⭐ 랭킹보드가 **언제 것을 줄 세우나**.
 * ⚠️ 서버 util/periodKey.js PERIODS · 런타임 types.ts RankingPeriod 와 같아야 한다.
 * ⚠️ 주 경계는 **날짜 구간**이다 — 1~7일이 첫째주. 요일과 무관하고 달의 마지막 주는 1~3일 짧다
 *    (화면 표기 "10월 첫째주"와 날짜가 정확히 맞는 쪽을 골랐다. periodKey.js 주석).
 */
export const RANKING_PERIODS = [
	{ value: "all", label: "전체 기간", hint: "처음부터 지금까지." },
	{ value: "week", label: "주간", hint: "화면에 \"10월 첫째주\"처럼 나옵니다. 1~7일이 첫째주입니다." },
	{ value: "month", label: "월간", hint: "화면에 \"10월\"처럼 나옵니다." },
];

/** 랭킹 설정 기본값. ⚠️ 서버 getRanking 의 기본값과 같아야 한다. */
export const newRanking = () => ({ source: "wallet", period: "all", targetTileId: "", limit: 20, unit: "" });

/**
 * 붙박이 화면 종류. ⚠️ 서버 BUILTIN_SCREENS · 런타임 BuiltinScreen 과 같아야 한다.
 * ⚠️ 늘릴 때는 세 곳을 함께 고칠 것 — 하나만 늘리면 저작자가 고른 화면이 안 열린다.
 */
export const BUILTIN_SCREENS = [
	{
		value: "diary",
		label: "받은 기록 (다이어리)",
		hint: "레시피로 받은 결과를 날짜순으로 보여줍니다. 피그마의 '행운빵 다이어리'.",
	},
	{
		value: "ranking",
		label: "랭킹보드",
		hint: "재화를 많이 모은 사람 순으로 보여줍니다. 로그인하지 않아도 볼 수 있고, 닉네임과 보유량만 나옵니다. 재화를 안 쓰는 런처라면 넣지 마세요.",
	},
];

/** 타일이 놓이는 자리. */
export const TILE_AREAS = [
	{ value: "grid", label: "아래 큰 버튼", hint: "하단 2열 격자. 가장 많이 누르는 것을 둡니다." },
	{ value: "rail", label: "오른쪽 작은 아이콘", hint: "세로 아이콘 줄. 보조 기능을 둡니다." },
];

/** 새 타일 한 장. ⚠️ 런타임 Tile 과 키가 같아야 한다. */
export const newTile = () => ({
	id: `tl${Math.random().toString(36).substring(2, 10)}`,
	label: "",
	area: "grid",
	presentation: "link",
	iconAssetId: null,
	url: "",
	linkBotId: "",
	target: "self",
	embedUrl: "",
	recipeId: "",
	builtinScreen: "",
	gate: {},
});

/**
 * 테마 색 토큰 (설계 §2-3).
 *
 * ⚠️ 런타임 LAUNCHER/src/config.ts DEFAULT_THEME 과 **같은 기본값**이어야 한다.
 *    다르면 "빌더에 보이는 색과 실제 화면 색이 다르다"가 된다(게임에서 겪은 함정).
 */
export const THEME_COLORS = [
	{ key: "background", label: "바탕", fallback: "#f5efe6" },
	{ key: "surface", label: "카드·말풍선", fallback: "#fdf6ec" },
	// ⚠️ 이름표·칩의 **바탕**. 바탕과 글자 사이의 중간 톤이다.
	//    `muted`(흐린 글자) 로 때우면 안 된다 — 글자 역할이라 칩 바탕으로 쓰면 시안보다 어둡다.
	{ key: "chip", label: "이름표·칩 바탕", fallback: "#8b7355" },
	{ key: "chipText", label: "칩 위 글자", fallback: "#ffffff" },
	{ key: "primary", label: "강조", fallback: "#e8a33d" },
	// ⚠️ 강조색 **위에 얹히는** 글자. 게임 테마 accentText 와 같은 역할이다.
	//    시안 온보딩 버튼이 갈색 바탕 + 흰 글자라, 이 토큰이 없으면 표현할 수 없다.
	{ key: "primaryText", label: "강조 위 글자", fallback: "#4a3728" },
	{ key: "text", label: "글자", fallback: "#4a3728" },
	{ key: "muted", label: "흐린 글자", fallback: "#8b7355" },
	{ key: "border", label: "테두리", fallback: "#e0d2bd" },
	// ⭐ 아래 아홉은 **비워 두면 다른 칸을 따라간다**(`follows`). 시안이 '강조'·'테두리' 자리에 갈색을 여러 개
	//    써서 따로 둔 칸이다(2026-10-30) — 진행바 채움 #b67947 · 결과/안내 화면 버튼 테두리 #552e21 이
	//    온보딩 버튼(#6d472e)·일반 테두리(#5d402e)와 다르다. 런타임 styles.css 가 `var(--lc-progress, var(--lc-primary))`
	//    처럼 받으므로 **비어 있으면 지금까지와 똑같이** 보인다(마이그레이션 0).
	// ⚠️ 키는 런타임 types.ts Theme.colors · theme.ts applyTheme 과 같아야 한다.
	{ key: "progress", label: "진행바 채움", follows: "primary" },
	{ key: "progressTrack", label: "진행바 바탕", follows: "surface" },
	{ key: "progressBorder", label: "진행바 테두리", follows: "border" },
	// 붓 테두리에서만 쓰인다(주 버튼은 반듯한 선 묶음엔 테두리가 없다).
	{ key: "buttonBorder", label: "버튼 테두리", follows: "border" },
	// 뒤로가기·닫기 꺾쇠, 기본 설정 아이콘(.lc-iconbtn 글자색).
	{ key: "icon", label: "아이콘", follows: "text" },
	// 꺼진 버튼(경품 이벤트 '내일 다시 도전해요!'·'이미 당첨되었어요!' 등, 시안 2154:1752). 테두리 없이 면·글자만.
	{ key: "buttonDisabled", label: "꺼진 버튼", follows: "surface" },
	{ key: "buttonDisabledText", label: "꺼진 버튼 글자", follows: "muted" },
	// 결과 화면(시안 1599:5113) — 본문 상자 테두리 #704629 · 카드 이름칩 #ae806a 가 일반 테두리·이름표 색과 다르다.
	{ key: "resultBorder", label: "결과 본문 테두리", follows: "border" },
	{ key: "badge", label: "카드 이름칩", follows: "chip" },
];

/**
 * 고를 수 있는 글꼴 — **봇 빌더·게임 빌더와 같은 목록**이다
 * (UI/src/views/PageDesign.svelte fontList · game-builder/gameSlots.js GAME_FONTS).
 * 저작자가 챗봇·게임에서 쓰던 글꼴을 런처에서도 그대로 고를 수 있어야 해서 일부러 맞췄다.
 *
 * ⚠️ `key` 가 곧 CSS `font-family` 이름이다. 서버 util/gameFonts.js 의 FONT_FILES 표와
 *    **같아야 한다** — 거기 없는 키를 저장하면 셸에 @font-face 가 안 박혀 글꼴이 조용히 안 먹는다.
 * ⚠️ 글꼴 파일은 이미 같은 S3 버킷(common/builder/fonts/)에 있다. 새로 올릴 것이 없고,
 *    런처 셸은 `fontFaceCssAll()` 로 **목록 전체**를 선언하므로 서버 수정도 없다
 *    (전부 선언해도 받는 파일은 고른 하나다 — gameFonts.js 주석).
 * ⚠️ 빌더에서 샘플이 그 글꼴로 보이는 건 public/static/css/avatalk-builder.webflow.css 가
 *    @font-face 를 갖고 있기 때문이다. 목록에 새 글꼴을 더하려면 그쪽도 같이 봐야 한다.
 */
export const LAUNCHER_FONTS = [
	{ key: "", label: "기본", sample: "가나다 Ab" },
	{ key: "NotoSansKRBold", label: "노토산스 Bold", sample: "노토산스" },
	{ key: "NanumSquareBold", label: "나눔 스퀘어", sample: "나눔 스퀘어" },
	{ key: "NanumSquareRound", label: "나눔 라운드", sample: "나눔 라운드" },
	{ key: "Katuri", label: "안동 엄마 까투리", sample: "안동엄마까투리" },
	{ key: "Neodgm", label: "neo둥근모", sample: "neo둥근모" },
	{ key: "SeoulHangangExtraBold", label: "서울 한강", sample: "서울한강" },
	{ key: "IMHyemin", label: "IM혜민체", sample: "IM혜민체" },
	{ key: "UhBeeZZIBA", label: "어비찌빠체", sample: "어비찌빠체" },
	{ key: "OwnglyphPDH", label: "온글잎 PDH", sample: "온글잎 손글씨" },
];

/**
 * 고르면 색·모서리·테두리가 한꺼번에 채워지는 묶음. 낱개로 칠하기 전에 여기서 시작하면 된다.
 * (게임 빌더 gameSlots.js THEME_PRESETS 과 같은 장치다)
 *
 * ⭐ `warm` 은 **런처·게임이 같은 피그마에서 나온 한 가족**이다 — 세 값이 글자까지 똑같다:
 *      border #5d402e (시안 talkBoard 테두리)  ·  text #423328 (시안 본문)
 *      primary #ffd62c (= 게임 accent)
 *    그래서 런처에서 `warm`, 게임에서 '밝은 동화'를 고르면 테마를 전달하지 않아도 결이 맞는다.
 *    (테마 **자동 전파**는 접었다 — 설계 §2-3a. 전파 대신 **같은 프리셋을 양쪽에 두는** 방식이다)
 *
 * ⚠️ 모양(radius·borderStyle)까지 들어 있다. 색만 바꾸는 묶음이 아니다.
 * ⚠️ `fontFamily` 는 **일부러 안 넣는다** — 글꼴은 색과 다른 축이라, 프리셋을 고를 때
 *    저작자가 고른 글꼴이 날아가면 안 된다(emitTheme 이 패치 병합이라 안 건드리면 보존된다).
 * ⚠️ `basic` 은 런타임 LAUNCHER/src/config.ts DEFAULT_THEME 과 **같은 값이어야 한다** —
 *    "아무것도 안 고른 상태"와 "기본을 고른 상태"가 달라 보이면 안 된다.
 */
export const THEME_PRESETS = [
	{
		key: "basic",
		label: "기본",
		sample: "무던한 크림",
		theme: {
			borderStyle: "clean",
			radius: 16,
			colors: {
				background: "#f5efe6",
				surface: "#fdf6ec",
				// 지금까지 muted 로 그려지던 값 그대로 — 기존 런처의 겉모습이 안 바뀐다.
				chip: "#8b7355",
				chipText: "#ffffff",
				primary: "#e8a33d",
				// ⚠️ 주황 위 흰 글자는 대비 2.2:1 — 본문색을 올려 5.2:1(AA) 로.
				primaryText: "#4a3728",
				text: "#4a3728",
				muted: "#8b7355",
				border: "#e0d2bd",
			},
		},
	},
	{
		key: "warm",
		label: "밝은 동화",
		sample: "베이커리·아기자기 (시안)",
		theme: {
			// 시안의 테두리가 브러쉬라 이 묶음은 '붓 · 곧은 선'이 기본이다.
			borderStyle: "brushStraight",
			radius: 16,
			colors: {
				// ⭐ 아래 값은 전부 **피그마 렌더를 2x 로 내보내 픽셀을 직접 집은 것**이다
				//    (2026-10-06). 노드 JSON 만 보면 틀린다 — 말풍선 fill 은 JSON 상 #ffffff 지만
				//    실제로 칠해지는 건 #fcedd6 이고, 버튼은 JSON 에 면이 아예 안 잡힌다.
				background: "#f9ecdb", // 메인화면 바탕
				surface: "#fcedd6", // 말풍선·타일 면 (= 게임 warm.panel #fdedd6 과 같은 값)
				chip: "#b98866", // 이름표 바탕 (실측)
				chipText: "#ffffff", // 시안 그대로 — 크림을 올리면 대비가 2.7:1 로 떨어진다
				// ⚠️ 예전엔 #ffd62c(노랑)였다 — 그건 **게임 화면** 색이고 런처 화면엔 안 쓰인다.
				//    런처의 강조는 온보딩 버튼의 갈색이다.
				// 주 버튼 바탕 — 시안 Button 컴포넌트(정보 입력·결과·이벤트 팝업 버튼·당첨 제목 전부 #552e21).
				// ⚠️ 예전 #6d472e(옛 온보딩 버튼 실측)였다 — 최신 시안과 달라 2026-11-02 바꿨다. 버튼 테두리와 같은 색이다(시안도 면=선).
				primary: "#552e21",
				primaryText: "#ffffff", // 그 위 글자
				text: "#423328", // 본문
				// ⚠️ 시안에 '보조 글자' 색이 따로 없다(회색들은 범용 화면에서 온 것). 역할이 같은
				//    게임 textMuted 를 쓴다 — 가족을 맞추는 쪽이 눈에 맞는다.
				muted: "#8a6f52",
				border: "#5d402e", // 시안 talkBoard 테두리 = 게임 border
				// 시안 loadingBar(1434:975) — 채움·바탕이 강조색·카드 바탕과 다른 색이다.
				progress: "#b67947",
				progressTrack: "#fff9f4",
				// 진행바 테두리 — 일반 테두리(#5d402e)보다 밝다. 그 색으로 그으면 시안보다 굵어 보였다(2026-11-02).
				progressBorder: "#704629",
				// 시안 Button 테두리(결과·결과 전·안내 화면). 일반 테두리(#5d402e)로 두면 어둡게 깐 결과 전 화면에서
				// 버튼 바깥 결이 배경보다 9 밖에 안 어두워 묻혔다(시안 23).
				buttonBorder: "#552e21",
				// 뒤로가기 꺾쇠 등 아이콘 — 시안 Nav 꺾쇠 #51463f(글자 #423328 보다 밝다).
				icon: "#51463f",
				// 꺼진 버튼 — 시안 2154:1752('내일 다시 도전해요!'·'이미 당첨되었어요!'). 테두리 없음.
				buttonDisabled: "#e8ddd5",
				buttonDisabledText: "#a28c80",
				// 결과 화면 — 본문 상자 테두리 · 카드 이름칩(시안 1599:5125 · 1599:5128).
				resultBorder: "#704629",
				badge: "#ae806a",
			},
		},
	},
];

/**
 * 테두리를 긋는 방식. **게임 빌더와 같은 값이다**(UI game-builder/gameSlots.js BORDER_STYLES).
 * 색이 아니라 **선을 어떻게 긋느냐**라 색 토큰과 따로 둔다.
 *
 * ⚠️ 피그마 시안의 테두리는 피그마 고유의 **브러쉬 스트로크**다(SCATTER 브러쉬) — 노드
 *    데이터상으로는 평범한 SOLID 2px 로 보이는데 렌더는 울퉁불퉁하다. 색·굵기만 옮기면
 *    그 맛이 통째로 빠진다.
 * ⚠️ value 를 같은 문자열로 쓰는 곳: 여기 · 런타임 LAUNCHER/src/types.ts BorderStyle ·
 *    theme.ts BRUSH_STYLES · brushBorder.ts WOBBLE/MIN_W · 이 폴더 brushBorder.js(샘플).
 *    하나만 늘리면 조용히 반듯한 선으로 떨어진다.
 *
 * ⭐ 런처는 **시안 붓 원본을 경로 따라 찍는다**(LAUNCHER/src/brushBorder.ts — 피그마 SCATTER 붓과
 *    같은 방식, 요소마다 border-image 로 붙인다). 게임은 Phaser 캔버스에 붓자국을 찍는다(GAME sketch.ts).
 *    (예전 런처는 SVG 필터로 결을 흉내 냈다 — 시안과 원리가 달라 흐린 선밖에 안 나와 걷어냈다. 2026-10-30)
 */
export const BORDER_STYLES = [
	{ value: "clean", label: "반듯한 선", hint: "곧고 일정한 두께" },
	{ value: "sketch", label: "손그림 선", hint: "손으로 그은 듯 흔들리는 테두리" },
	{ value: "brushStraight", label: "붓 · 곧은 선", hint: "반듯한 선에 붓 질감 (시안이 이 방식)" },
	{ value: "brush", label: "붓 · 손그림", hint: "손으로 그은 선에 붓 질감" },
];

/** 저작자가 안 고르면 반듯한 선. ⚠️ 런타임 config.ts DEFAULT_THEME.borderStyle 과 같아야 한다. */
export const DEFAULT_BORDER_STYLE = "clean";

/**
 * 결과 본문을 무엇이 만드나 (설계 §5-3).
 * ⚠️ value 는 서버 util/launcherConfig.js RESULT_SOURCES 와 같아야 한다.
 */
export const RESULT_SOURCES = [
	{
		value: "pool",
		label: "미리 쓴 글에서 뽑기",
		hint: "카드마다 적어 둔 글이 그대로 나옵니다. 비용 0 · 즉시 · 내용이 늘 같습니다.",
	},
	{
		value: "pool+llm",
		label: "뽑고, 그 사람에 맞춰 쓰기",
		hint: "카드(이름·그림)는 뽑고 본문만 AI가 씁니다. 가장 자연스럽지만 호출 비용이 듭니다.",
	},
	{
		value: "llm",
		label: "전부 AI가 쓰기",
		hint: "카드 없이 본문만 만듭니다. 그림이 필요 없을 때 씁니다.",
	},
];

/** 결과 화면 하단 버튼이 하는 일. ⚠️ 서버 ACTION_KINDS · 런타임과 같아야 한다. */
export const ACTION_KINDS = [
	{ value: "home", label: "홈으로" },
	/**
	 * ⭐ **앞 결과로 돌아가기** — 물려주기(carry)의 짝이다.
	 *    A(운세) → [강화하기] → B(강화) → **[뒤로가기] → A** 로 되돌아간다.
	 * ⚠️ '다른 레시피 열기'로 A 를 다시 여는 것과 **다르다**: 그쪽은 레시피를 **다시 실행**해서
	 *    로딩 화면을 또 거치고(하루 1회짜리는 캐시가 나와 내용만 같다), 이쪽은 런타임이 들고 있던
	 *    **그 결과를 그대로** 되돌린다(서버 왕복 0).
	 * ⚠️ 돌아갈 앞 결과가 없으면(타일로 바로 들어온 첫 화면) 홈으로 닫힌다.
	 */
	{ value: "back", label: "앞 결과로 돌아가기" },
	{ value: "recipe", label: "다른 레시피 열기" },
	{ value: "tile", label: "타일 열기" },
	{ value: "link", label: "링크 열기" },
];

/**
 * ⭐ **결과 전(공개) 화면** 연출 — 뽑힌 카드 그림이 어떻게 나타나고 뒤에서 무엇이 빛나는지.
 * ⚠️ value 는 런타임 LAUNCHER/src/types.ts `RevealEntrance`·`RevealGlow` 와 같아야 한다.
 *    서버는 검사하지 않고 넘긴다 — 모르는 값은 런타임(RevealStage)이 첫 항목(기본값)으로 눕힌다.
 * ⚠️ 각 목록의 **첫 항목이 기본값**이다(시안: 톡 튀어나오기 · 돌아가는 빛줄기).
 */
export const REVEAL_ENTRANCES = [
	{ value: "pop", label: "톡 튀어나오기", hint: "작게 시작해 살짝 커졌다가 제자리에 앉습니다." },
	{ value: "rise", label: "아래서 떠오르기", hint: "아래에서 올라와 자리에 앉습니다." },
	{ value: "fade", label: "서서히 나타나기", hint: "흐릿하게 시작해 또렷해집니다. 가장 조용합니다." },
];
export const REVEAL_GLOWS = [
	{ value: "rays", label: "돌아가는 빛줄기", hint: "카드 뒤에서 빛줄기가 천천히 돕니다." },
	{ value: "halo", label: "은은한 빛 번짐", hint: "돌지 않고 숨쉬듯 커졌다 작아집니다. 배경이 복잡할 때 덜 어지럽습니다." },
	{ value: "none", label: "빛 없음", hint: "카드만 나타납니다." },
];
/**
 * 나타난 뒤 카드 움직임. ⚠️ 런타임 types.ts `RevealMotion` 과 같아야 한다. 첫 항목이 기본값.
 * ⚠️ 옛 저작값은 `bounce: false`(스위치)일 수 있다 — `motion` 이 없으면 런타임이 그걸 '없음'으로 읽는다.
 */
export const REVEAL_MOTIONS = [
	{ value: "bounce", label: "통 (한 번씩)", hint: "살짝 커졌다 돌아오고 잠깐 쉽니다." },
	{ value: "heartbeat", label: "두근두근", hint: "심장처럼 두 번 뛰고 쉽니다." },
	{ value: "none", label: "없음", hint: "나타난 뒤 가만히 있습니다." },
];
/** 옛 스위치(`bounce`)까지 읽어 지금 고른 움직임을 낸다(런타임 RevealStage 와 같은 규칙). */
export const revealMotionOf = (reveal) => {
	const v = reveal?.motion;
	if (REVEAL_MOTIONS.some((m) => m.value === v)) return v;
	return reveal?.bounce === false ? "none" : "bounce";
};

/** 배경 어둡게(%). ⚠️ 런타임 상한 80 — 그 위는 배경이 거의 안 보인다. 기본 50 = 시안. */
export const REVEAL_DIMS = [
	{ value: 0, label: "안 함" },
	{ value: 20, label: "살짝 (20%)" },
	{ value: 35, label: "중간 (35%)" },
	{ value: 50, label: "진하게 (50%)" },
	{ value: 65, label: "아주 진하게 (65%)" },
];
export const DEFAULT_REVEAL_DIM = 50;

/**
 * 결과 전 화면 **말풍선 바탕**. ⚠️ 값 규약은 런타임 RevealStage `bubbleOf` 와 같아야 한다:
 *    ''(기본) = 흰색 · 'theme' = 테마 '카드·말풍선' 색 · 그 밖 = '#hex'(직접 고르기).
 * ⭐ 기본이 테마 색이 아니라 흰색인 이유: 이 화면은 배경을 **어둡게 덮은 장면**이다. 테마 말풍선 색은
 *    밝은 장면(홈·로딩)에 맞춰 고른 색이라(크림 등), 70% 로 비치면 어두운 바탕과 섞여 탁해진다.
 */
export const REVEAL_BUBBLE_DEFAULT = "#ffffff";
export const REVEAL_BUBBLES = [
	{ value: "", label: "흰색" },
	{ value: "theme", label: "테마 말풍선 색" },
	{ value: "custom", label: "직접 고르기" },
];

/** '#rgb' / '#rrggbb' → [r,g,b]. 모양이 아니면 null. */
const hexRgb = (hex) => {
	let h = String(hex || "").trim().replace(/^#/, "");
	if (h.length === 3) h = h.split("").map((c) => c + c).join("");
	if (!/^[0-9a-f]{6}$/i.test(h)) return null;
	return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};
/**
 * 두 색의 **대비율**(WCAG, 1~21). 모르는 색이면 null.
 * ⚠️ 말풍선은 70% 반투명이라 실제 바탕은 이보다 조금 어둡다 — 여기선 불투명 색끼리 대충 본다.
 *    글자 대비 4.5 미만이면 '읽기 어렵다' 쪽이다.
 */
export const contrastRatio = (a, b) => {
	const x = hexRgb(a);
	const y = hexRgb(b);
	if (!x || !y) return null;
	const lum = (rgb) =>
		rgb
			.map((v) => {
				const c = v / 255;
				return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
			})
			.reduce((sum, c, i) => sum + c * [0.2126, 0.7152, 0.0722][i], 0);
	const [hi, lo] = [lum(x), lum(y)].sort((p, q) => q - p);
	return (hi + 0.05) / (lo + 0.05);
};

/**
 * ⭐ 레시피 **종류**. ⚠️ 런타임 types.ts RecipeKind · 서버 launcherConfig.normalizeRecipe 와 같아야 한다.
 *   generate  안내 → 만드는 중 → (결과 전) → 결과 — 운세처럼 '만들어 주는' 것. kind 가 없으면 이것(옛 저작값)
 *   event     경품 이벤트 — 기간 동안 참여하고 경품을 받는다. 자기 화면(런타임 EventScreen)을 쓴다
 */
export const RECIPE_KINDS = [
	{ value: "generate", label: "만들어 주기 (운세 등)" },
	{ value: "event", label: "경품 이벤트" },
];

/**
 * ⭐ 경품 이벤트의 **당첨 방식**. 공통 틀(기간·경품·수령·팝업)은 같고 '누가 당첨되나'만 다르다.
 * ⚠️ 서버 util/launcherEvent.js MODES · 런타임 types.ts EventMode 와 같아야 한다.
 * ⚠️ `ready:false` 는 **자리만 잡아 둔 것**이다 — 고를 수 없게 띄운다(서버에 아직 처리기가 없다).
 */
export const EVENT_MODES = [
	{ value: "draw", label: "즉석 뽑기", ready: true, hint: "누를 때마다 정한 확률로 당첨/꽝이 바로 나옵니다(캡슐 뽑기 등)." },
	{ value: "ranking", label: "순위 보상 (준비 중)", ready: false, hint: "기간이 끝나면 게임 점수 등의 순위로 상위 몇 명에게 경품을 줍니다." },
];

/** 경품 한 개. ⚠️ 확률은 여기 없다 — 방식(draw.rates)에 있다(순위 보상엔 확률이 없다). */
export const newPrize = () => ({
	id: `pz${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	brand: "",
	imageAssetId: null,
	/** 비우면 **제한 없음**. 경품이 걸린 이벤트라 빌더가 그 사실을 경고한다. */
	quantity: 10,
});

/**
 * 새 경품 이벤트 한 벌. 문구 기본값은 **비워 둔다** — 런타임이 도메인 중립 기본 문구를 갖고 있고,
 * 여기 채우면 저작자가 '이미 쓴 글'로 착각한다(결과 전 말풍선 placeholder 사고와 같은 이유).
 */
export const newEventRecipe = () => ({
	id: `rc${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	kind: "event",
	event: {
		mode: "draw",
		startDate: "",
		endDate: "",
		periodText: "",
		backgroundAssetId: null,
		titleAssetId: null,
		// 타이틀 그림 통통+시소 움직임. ⚠️ 없으면 켜짐으로 읽는다(옛 저작값 호환).
		titleMotion: true,
		// 메인 카드 위 툴팁(시안 1914:25327). 문구가 비면 안 뜬다.
		tip: { text: "", iconAssetId: null },
		prizes: [],
		info: { title: "", text: "", note: "", confirmLabel: "" },
		win: { title: "", desc: "", claimLabel: "", claimUrl: "", showCode: true },
		lose: { title: "", desc: "", buttonLabel: "", imageAssetId: null },
		draw: {
			rates: {},
			dailyLimit: 1,
			winLimit: 1,
			dailyWinCap: 0,
			buttonLabel: "",
			doneLabel: "",
			wonLabel: "",
			chanceLabel: "",
			durationMs: 3000,
			machineAssetId: null,
			/** 손잡이(선택) — 기계 그림과 따로 올리면 뽑을 때 돈다. */
			handleAssetId: null,
			// 바닥(카펫, 선택) — 기계 밑에 깔린다. 비우면 없다(기본 그림 없음).
			floorAssetId: null,
			capsuleAssetIds: [],
		},
	},
});

/** 새 레시피 한 벌. ⚠️ 런타임 Recipe 와 키가 같아야 한다. */
export const newRecipe = () => ({
	id: `rc${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	intro: { text: "", confirmLabel: "", imageAssetId: null },
	loading: { title: "", desc: "", minMs: 1800, imageAssetId: null },
	/** 결과 전 화면. **꺼진 채로** 시작한다 — 켜기 전엔 지금처럼 로딩 → 결과다. */
	reveal: { enabled: false, text: "", confirmLabel: "", backgroundAssetId: null, lightAssetId: null },
	/** ⚠️ `lead` = 결과 화면 본문 **맨 앞 줄**(저작 문구 + 토큰). 서버가 본문 앞에 붙여 저장한다. */
	result: { source: "pool", badgeStyle: "pill", lead: "", tagline: "", pool: [], llm: { prompt: "", maxChars: 900 } },
	actions: [{ label: "홈으로 가기", kind: "home", target: "" }],
	gate: {},
});

/** 결과 카드 한 장. */
export const newCard = () => ({
	id: `cd${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	imageAssetId: null,
	/** 이름 붙은 **다른 모습**들 {키: assetId}. 이어받는 레시피가 그 키를 쓰겠다고 선언하면
	 *  이 카드 줄에 그 칸이 자동으로 생긴다(RecipeContext.carriedKeys). */
	images: {},
	weight: 1,
	fallbackBody: "",
});

export const DEFAULT_RADIUS = 16;

/**
 * 저작 문구의 글자 크기 **배율**.
 * ⚠️ 런타임 LAUNCHER/src/config.ts DEFAULT_THEME.textScale 과 같아야 한다 — 두 벌이 되면
 *    "빌더에 보이는 것과 실제가 다르다"가 된다.
 * ⚠️ 상한을 1.6 으로 둔 이유: 그 위로 올리면 타일 문구가 두 줄을 넘겨 격자 높이(132px)를
 *    밀어내고, 아래는 0.8 아래로 가면 말풍선이 12px 밑으로 내려가 읽기가 어렵다.
 *    런타임 theme.ts 는 0.5~2 를 받아 주지만(깨진 설정 방어), **저작으로 고를 수 있는 범위**는
 *    여기가 정한다.
 */
export const DEFAULT_TEXT_SCALE = 1;
export const TEXT_SCALE_MIN = 0.8;
export const TEXT_SCALE_MAX = 1.6;

/* ── 저작 문구의 치환 토큰 ────────────────────────────────────────────────────────
 * ⚠️⚠️ 규칙은 **세 곳**이 같아야 한다:
 *    · 런타임 화면   LAUNCHER/src/tokens.ts          (fillTokens)
 *    · 서버 프롬프트 SERVER/module/util/launcherRecipe.js (fillTokens)
 *    · 여기(안내·점검)
 *    여기만 늘리면 "빌더가 쓸 수 있다고 했는데 화면에서 글자 그대로 나온다"가 된다.
 */

/** 어디서나 쓸 수 있는 토큰. ⚠️ `{profile.키}` 는 저작자가 만든 칸마다 생기므로 여기 없다. */
/**
 * ⭐ **뜻이 정해져 있는 키.** 막는 게 아니라 — 보통 칸처럼 만들고 쓰면 되지만, 그 값이 어디에
 * 더 쓰이는지 저작자가 알아야 하는 키다.
 *
 * `nickname` — **랭킹에 보이는 이름**이고 소셜 로그인이 미리 채운다. 저장도 다른 칸과 달리
 * 전용 컬럼(LAUNCHER_USER.NICKNAME)으로 간다 — 다만 그건 서버 사정이고, 저작·화면에서는
 * `profile.nickname` 으로 **다른 칸과 똑같이** 다룬다(Launcher.saveProfile / publicUser).
 * ⚠️ 예전엔 전용 칸을 드롭다운에 따로 띄우고 이 키를 **막았다.** 그러면 "온보딩에서 받은 값이
 *    수정 화면에 안 나온다"가 생긴다(저장 자리가 갈려서) — 지금은 한 갈래다.
 */
export const MEANINGFUL_PROFILE_KEYS = {
	nickname: "랭킹에 보이는 이름입니다. 소셜 로그인 이름이 미리 채워집니다.",
};
export const meaningOfProfileKey = (k) => MEANINGFUL_PROFILE_KEYS[String(k || "").trim()] || "";

export const TEXT_TOKENS = [
	{ token: "{date}", hint: "오늘 날짜 — \"10월 12일 월요일\" (요일까지 들어 있다)" },
	{ token: "{weekday}", hint: "요일만 — \"월요일\"" },
];

/**
 * 저작 문구에서 **선언되지 않은** `{profile.키}` 를 찾는다.
 *
 * ⭐ 이게 왜 필요한가: `{profile.nickname}` 이라고 썼는데 실제 칸 키가 `nickname2` 면 런타임은
 *    **조용히 빈칸**으로 지운다(값이 아직 없는 것과 구별할 수 없어서 그게 맞다). 저작자 눈에는
 *    "토큰이 안 먹는다"로만 보인다 — 그래서 **저작 시점에** 잡아 준다.
 * ⚠️ `{name}`·`{date}` 는 보지 않는다(여기서 찾는 건 `profile.` 접두어뿐).
 */
/**
 * 저작자에게 보여줄 **토큰 목록**.
 *
 * ⚠️ 닉네임도 **그냥 한 칸**이라 특별 취급이 없다 — 저작자가 만들면 `{profile.nickname}` 이 뜬다.
 * ⚠️ 두 탭([화면]·[신원])이 **같은 목록**을 봐야 한다 → 각자 조립하지 말고 이걸 쓸 것.
 */
export const tokenListOf = (profileFields) => [
	...TEXT_TOKENS.map((t) => t.token),
	...(profileFields || [])
		.map((f) => f?.key)
		.filter(Boolean)
		.map((k) => `{profile.${k}}`),
];

export const unknownProfileTokens = (texts, profileFields) => {
	// ⚠️ `{profile.nickname}` 은 저작자가 칸을 안 만들었어도 **항상 값이 있다**(소셜 로그인이
	//    채운다) → '없는 칸'으로 경고하지 않는다.
	const declared = new Set([
		...Object.keys(MEANINGFUL_PROFILE_KEYS),
		...(profileFields || []).map((f) => f?.key).filter(Boolean),
	]);
	const found = new Set();
	for (const t of texts || []) {
		for (const m of String(t || "").matchAll(/\{profile\.([a-zA-Z0-9_]+)\}/g)) {
			if (!declared.has(m[1])) found.add(m[1]);
		}
	}
	return [...found];
};
